import { TextLinkWidget, Tooltip, } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { classes,style } from 'typestyle';

import { HelpService } from '~src/commonInfra/services/HelpService';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import isOnlineEnvironment = SessionServerSelectors.isOnlineEnvironment;

const tooltipStyle = style({
  display: 'flex',
  alignItems: 'center',
});

const smallPaddingStyle = style({
  paddingLeft: 3,
  width: 16,
});

const subSettingsWrapper = style({
  display: 'flex',
});

export type FlowParametersSiteSettingSectionProps = SiteSettingsForSiteAdminProps<'flowParametersEnabled' | 'flowParametersAnyTypeEnabled' | 'flowParametersSystemTypeEnabled'>;

export const FLOWPARAMETERS_SITE_SETTINGS_TESTID = 'flow-parameters-site-setting-section-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_HELP_TESTID = 'flow-parameters-site-settings-help-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_CHECKBOX_TESTID = 'flow-parameters-on-site-checkbox-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_CHECKBOX_TESTID = 'flow-parameters-any-type-on-site-checkbox-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_HELP_TESTID = 'flow-parameters-any-type-on-site-help-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_TOOLTIP_TESTID = 'flow-parameters-tooltip-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_TOOLTIP_TESTID = 'flow-parameters-any-type-tooltip-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_TOOLTIP_TESTID = 'flow-parameters-system-type-tooltip-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_TOOLTIP_ICON_TESTID = 'flow-parameters-system-type-tooltip-icon-test-id';
export const FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_CHECKBOX_TESTID = 'flow-parameters-system-type-on-site-checkbox-test-id';

export function FlowParametersSiteSettingSection(props: FlowParametersSiteSettingSectionProps): JSX.Element {
  const isOnline = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const flowParametersSchedulingEnabledOnServer = useSelector(SessionServerSelectors.flowParametersEnabled);
  const flowParametersAnyTypeEnabledOnServer = useSelector(SessionServerSelectors.flowParametersAnyTypeEnabled);
  const flowParametersSystemTypeEnabled = useSelector(SessionServerSelectors.flowParametersSystemTypeEnabled);
  const formik = useFormikContext<FlowParametersSiteSettingSectionProps>();
  const isSingleSiteServer = !useSelector(SessionServerSelectors.doesTheServerHaveMoreThanOneSite);

  return useShowSettingsSection() && (
    <SettingsSection
      title={Messages.flowParametersSettingHeader()}
      testId={FLOWPARAMETERS_SITE_SETTINGS_TESTID}
      description={
        <div>
          {Messages.flowParametersSiteSettingsBody()}
          <TextLinkWidget
            testId={FLOWPARAMETERS_SITE_SETTINGS_HELP_TESTID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('prep-conductor-flow-parameters')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      }>
      <div className={SettingsStyles.checkboxGroup}>
        {FlowParametersCheckbox(formik, isOnline || flowParametersSchedulingEnabledOnServer, isSingleSiteServer, isOnline)}
        {FlowParametersSystemTypeCheckbox(formik, isOnline || flowParametersSystemTypeEnabled, isSingleSiteServer, isOnline)}
        {FlowParametersAnyTypeCheckbox(formik, isOnline || flowParametersAnyTypeEnabledOnServer, isSingleSiteServer, isOnline)}
      </div>
    </SettingsSection>
  );
}

function FlowParametersCheckbox(formik: FormikContextType<FlowParametersSiteSettingSectionProps>, flowParametersEnabledOnServer: boolean, isSingleSiteServer: boolean, isOnline: boolean): JSX.Element {
  // checked if single site and server toggle is on, default to server value if true
  let isChecked: boolean = formik.values.settings.flowParametersEnabled;
  if (isSingleSiteServer && !isOnlineEnvironment) {
    isChecked = flowParametersEnabledOnServer;
  }
  return (
    <div className={classes(SettingsStyles.checkboxGroup, tooltipStyle)}>
      <FormikCheckbox
        name={'settings.flowParametersEnabled'}
        label={Messages.flowParametersSettingsAllowParametersCheckbox()}
        testId={FLOWPARAMETERS_SITE_SETTINGS_CHECKBOX_TESTID}
        checked={isChecked}
        disabled={!flowParametersEnabledOnServer}
      />
      {!flowParametersEnabledOnServer &&
        <Tooltip
          testId={FLOWPARAMETERS_SITE_SETTINGS_TOOLTIP_TESTID}
          getContent={() => { return <span>{Messages.flowParametersDisabledOnServer()}</span>; }}
        >
          <img className={smallPaddingStyle}
            data-tb-test-id={FLOWPARAMETERS_SITE_SETTINGS_TOOLTIP_TESTID}
            src={imgPath('data_prep_info.svg')}
            alt={Messages.flowParametersDisabledOnServer()}
          />
        </Tooltip>
      }
    </div>
  );
}

function FlowParametersAnyTypeCheckbox(
  formik: FormikContextType<FlowParametersSiteSettingSectionProps>,
  flowParametersAnyTypeEnabledOnServer: boolean,
  isSingleSiteServer: boolean,
  isOnline: boolean
): JSX.Element {
  // checked if single site and server toggle is on, default to server value if true
  let isChecked = formik.values.settings.flowParametersEnabled && formik.values.settings.flowParametersAnyTypeEnabled;
  if (isSingleSiteServer && !isOnline) {
    isChecked = flowParametersAnyTypeEnabledOnServer;
  }

  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <div className={subSettingsWrapper}>
        <FormikCheckbox
          name={'settings.flowParametersAnyTypeEnabled'}
          label={Messages.flowParametersSettingsAnyTypeParametersCheckbox()}
          testId={FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_CHECKBOX_TESTID}
          checked={isChecked}
          disabled={!formik.values.settings.flowParametersEnabled || !flowParametersAnyTypeEnabledOnServer}
        />
        {!flowParametersAnyTypeEnabledOnServer &&
          <Tooltip
            testId={FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_TOOLTIP_TESTID}
            getContent={() => { return <span>{Messages.flowParametersAnyTypeDisabledOnServer()}</span>; }}
          >
            <img className={smallPaddingStyle}
              data-tb-test-id={FLOWPARAMETERS_SITE_SETTINGS_ANY_TYPE_TOOLTIP_TESTID}
              src={imgPath('data_prep_info.svg')}
              alt={Messages.flowParametersAnyTypeDisabledOnServer()}
            />
          </Tooltip>
        }
      </div>
    </div>
  );
}

function FlowParametersSystemTypeCheckbox(
  formik: FormikContextType<FlowParametersSiteSettingSectionProps>,
  flowParametersSystemTypeEnabledOnServer: boolean,
  isSingleSiteServer: boolean,
  isOnline: boolean
): JSX.Element {
  // checked if single site and server toggle is on, default to server value if true
  let isChecked = formik.values.settings.flowParametersEnabled && formik.values.settings.flowParametersSystemTypeEnabled;
  if (isSingleSiteServer && !isOnline) {
    isChecked = flowParametersSystemTypeEnabledOnServer;
  }
  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <div className={subSettingsWrapper}>
        <FormikCheckbox
          name={'settings.flowParametersSystemTypeEnabled'}
          label={Messages.flowParametersSystemSettingCheckbox()}
          testId={FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_CHECKBOX_TESTID}
          checked={isChecked}
          disabled={!formik.values.settings.flowParametersEnabled || !flowParametersSystemTypeEnabledOnServer}
        />
        {!flowParametersSystemTypeEnabledOnServer &&
          <Tooltip
            testId={FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_TOOLTIP_TESTID}
            getContent={() => { return <span>{Messages.flowParametersSystemParametersDisabledOnServer()}</span>; }}
          >
            <img className={smallPaddingStyle}
              data-tb-test-id={FLOWPARAMETERS_SITE_SETTINGS_SYSTEM_TYPE_TOOLTIP_ICON_TESTID}
              src={imgPath('data_prep_info.svg')}
              alt={Messages.flowParametersSystemParametersDisabledOnServer()}
            />
          </Tooltip>
        }
      </div>
    </div>
  );
}

function useShowSettingsSection(): boolean {
  const doesTheServerHaveMoreThanOneSite = useSelector(SessionServerSelectors.doesTheServerHaveMoreThanOneSite);
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteAdmin = useSelector(SessionSiteSelectors.isSessionSiteAdmin);

  const isAllowedToChangeSetting = isServerAdmin || (isOnlineEnvironment && isSiteAdmin);
  return doesTheServerHaveMoreThanOneSite && isAllowedToChangeSetting;
}

/* for registering the component in settings registry */
export const FlowParametersSiteSettingSpecs = componentRegistryEntry({
  name: 'FlowParametersSiteSettingSection',
  component: FlowParametersSiteSettingSection,
});
